import { observer } from 'mobx-react-lite'
import type { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import {
  CheckoutViewSummary,
  CheckoutViewSummaryContent,
  CheckoutViewSummaryContentDivider,
  CheckoutViewSummaryContentList,
  CheckoutViewSummaryContentListQuoteItem,
  CheckoutViewSummaryContentListItemNote,
  CheckoutViewSummaryContentListItemSublist,
  CheckoutViewSummaryContentListItemSublistItem,
  CheckoutViewSummaryCouponChip,
  CheckoutViewSummaryHeader,
  CheckoutViewSummarySkeleton,
  CheckoutViewSummaryContentListResponseItem,
} from '@/features/jobs/drafting/checkout/components/CheckoutView/index'
import { formatMoney } from '@/utils/money-util'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import { match } from '@/utils/pattern-match-util'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { IsSkeletonContext } from '@/components/Skeleton'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil, whileActive } from '@/utils/rx-util'
import { filter, mergeMap, pairwise } from 'rxjs'
import { map } from 'rxjs/operators'
import { Quote } from '@/features/jobs/drafting/Quote'
import { useObservableValue } from '@/hooks/rx-hooks'
import { useMemo } from 'react'

interface CheckoutSummaryContainerProps {
  vm: DraftJobViewModel
}

/**
 * Checkout summary for the job.
 */
export const CheckoutSummaryContainer = observer(
  function CheckoutSummaryContainer({ vm }: CheckoutSummaryContainerProps) {
    if (!vm.checkoutVm.isQuoteEnabled) {
      return <CheckoutAnswersSummaryContainer vm={vm} />
    }

    return <CheckoutQuoteSummaryContainer vm={vm} />
  },
)

interface CheckoutAnswersSummaryContainerProps {
  vm: DraftJobViewModel
}

/**
 * Checkout summary displaying the question responses.
 */
const CheckoutAnswersSummaryContainer = observer(
  function CheckoutAnswersSummaryContainer({
    vm,
  }: CheckoutAnswersSummaryContainerProps) {
    const isSkeleton = vm.questionnaireVm.isFetchingQuestions
    const questions = vm.questionnaireVm.questionVms
    return (
      <>
        <CheckoutViewSummary>
          <CheckoutViewSummaryHeader title="Your responses" />
          <CheckoutViewSummaryContent>
            <IsSkeletonContext value={isSkeleton}>
              <CheckoutViewSummaryContentList>
                {questions.map((qvm) => (
                  <CheckoutViewSummaryContentListResponseItem
                    key={qvm.questionVm.id}
                    label={qvm.questionVm.title}
                    detail={qvm.questionVm.editor.textAnswer ?? 'N/A'}
                  />
                ))}
              </CheckoutViewSummaryContentList>
            </IsSkeletonContext>
          </CheckoutViewSummaryContent>
        </CheckoutViewSummary>
      </>
    )
  },
)

interface CheckoutQuoteSummaryContainerProps {
  vm: DraftJobViewModel
}

/**
 * Checkout summary displaying the quote.
 */
const CheckoutQuoteSummaryContainer = observer(
  function CheckoutQuoteSummaryContainer({
    vm: draftVm,
  }: CheckoutQuoteSummaryContainerProps) {
    const { checkoutVm: vm } = draftVm

    // Screen reader announcements for quote updates.
    const quoteUpdateAnnouncement = useObservableValue(
      useMemo(() => createQuoteUpdateAnnouncementsStream(draftVm), [draftVm]),
      { initial: '' },
    )

    if (!vm.quote) {
      if (vm.fetchQuote.pending) {
        return <CheckoutViewSummarySkeleton />
      }

      // No quote, and we're not fetching, so show the answers instead
      return <CheckoutAnswersSummaryContainer vm={draftVm} />
    }

    const isDisabled = vm.submit.pending
    return (
      <>
        <div className="sr-only" aria-live="polite" aria-atomic>
          {quoteUpdateAnnouncement}
        </div>
        <CheckoutViewSummary>
          <CheckoutViewSummaryHeader
            skeleton={vm.fetchQuote.pending ? 'details-only' : false}
            title="Summary"
            detail={vm.quote.price && <>{formatMoney(vm.quote.price.total)}</>}
            testId="summary-quote-header"
          />
          <CheckoutViewSummaryContent>
            <IsSkeletonContext value={vm.fetchQuote.pending}>
              <CheckoutViewSummaryContentList>
                <CheckoutViewSummaryContentListQuoteItem
                  labelTestId="summary-primary-item-label"
                  detailTestId="summary-primary-item-detail"
                  label={getQuotePrimaryLabel(vm.job)}
                  // Always expand the item list if there's no pricing, otherwise
                  // the summary will be looking rather bland.
                  alwaysExpanded={!vm.quote.price}
                  detail={
                    vm.quote.price && formatMoney(vm.quote.price.primaryAmount)
                  }
                >
                  {vm.quote.items.length > 0 && (
                    <>
                      <CheckoutViewSummaryContentListItemSublist>
                        {vm.quote.items.map((item, idx) => (
                          <CheckoutViewSummaryContentListItemSublistItem
                            key={idx}
                            testId={'summary-services-sublist-item'}
                          >
                            {item.name}
                          </CheckoutViewSummaryContentListItemSublistItem>
                        ))}
                      </CheckoutViewSummaryContentListItemSublist>

                      <CheckoutViewSummaryContentListItemNoteContainer
                        serviceType={vm.job.legend.serviceType}
                      />
                    </>
                  )}
                </CheckoutViewSummaryContentListQuoteItem>

                {vm.quote.price?.discount && (
                  <CheckoutViewSummaryContentListQuoteItem
                    label="Discount"
                    detail={formatMoney(-vm.quote.price.discount.amount)}
                    labelTestId="summary-discount-item-label"
                    detailTestId="summary-discount-item-detail"
                    extras={
                      <CheckoutViewSummaryCouponChip
                        code={vm.quote.price.discount.couponCode}
                        isSkeleton={vm.fetchQuote.pending}
                        onRemovePress={vm.billingVm.removeCoupon}
                        codeTestId="summary-discount-code"
                        removeButtonTestId="summary-discount-remove-button"
                        isDisabled={
                          isDisabled || vm.billingVm.isCouponActionPending
                        }
                      />
                    }
                  />
                )}

                {vm.quote.price && (
                  <>
                    <CheckoutViewSummaryContentListQuoteItem
                      emphasized
                      label="Total"
                      detail={formatMoney(vm.quote.price.total)}
                      labelTestId="summary-total-item-label"
                      detailTestId="summary-total-item-detail"
                    />
                  </>
                )}
              </CheckoutViewSummaryContentList>
              {vm.quote.price && (
                <>
                  {vm.quote.price.total !==
                    vm.quote.price.upfrontAmount + vm.job.paidAmount && (
                    <>
                      <CheckoutViewSummaryContentDivider />
                      <CheckoutViewSummaryContentList>
                        <CheckoutViewSummaryContentListQuoteItem
                          emphasized
                          label="Pay now"
                          detail={formatMoney(vm.quote.price.upfrontAmount)}
                          labelTestId="summary-amount-due-item-label"
                          detailTestId="summary-amount-due-item-detail"
                        />
                        <CheckoutViewSummaryContentListQuoteItem
                          label="Pay upon completion"
                          labelTestId="summary-remaining-amount-item-label"
                          detailTestId="summary-remaining-amount-item-detail"
                          detail={formatMoney(
                            vm.quote.price.total -
                              vm.quote.price.upfrontAmount -
                              vm.job.paidAmount,
                          )}
                        />
                      </CheckoutViewSummaryContentList>
                    </>
                  )}
                </>
              )}
            </IsSkeletonContext>
          </CheckoutViewSummaryContent>
        </CheckoutViewSummary>
      </>
    )
  },
)

function getQuotePrimaryLabel(job: DraftJob) {
  return match(job.legend.serviceType, {
    Tax: 'Full service',
    Consultation: 'Tax advisory',
    Bookkeeping: 'Bookkeeping',
    [match.DEFAULT]: 'Work required',
  })
}

interface CheckoutViewSummaryContentListItemNoteContainerProps {
  serviceType: LegendServiceType
}

/**
 * Checkout View Summary Content List Item Note Container
 */
const CheckoutViewSummaryContentListItemNoteContainer = observer(
  function CheckoutViewSummaryContentListItemNoteContainer({
    serviceType,
  }: CheckoutViewSummaryContentListItemNoteContainerProps) {
    switch (serviceType) {
      case 'Tax': {
        return (
          <CheckoutViewSummaryContentListItemNote>
            Additional forms may apply after checkout
          </CheckoutViewSummaryContentListItemNote>
        )
      }

      case 'Bookkeeping':
      case 'Misc': {
        return (
          <CheckoutViewSummaryContentListItemNote>
            Additional work may apply after checkout
          </CheckoutViewSummaryContentListItemNote>
        )
      }
      default: {
        return null
      }
    }
  },
)

/**
 * Creates announcements for screen readers when the quote is updated.
 * For example, when a discount is applied or removed, we'll notify
 * screen readers that the order's total has changed.
 *
 * @param vm
 */
function createQuoteUpdateAnnouncementsStream(vm: DraftJobViewModel) {
  const quote$ = toStream(() => vm.checkoutVm.quote, true).pipe(filterNonNil())
  const isActive$ = vm.checkoutVm.isCheckoutActive$.pipe(
    // We'll only be doing this while checkout is active,
    // and only if showing the quote is enabled.
    map((isActive) => isActive && vm.checkoutVm.isQuoteEnabled),
  )

  return quote$.pipe(
    pairwise(),
    filter(([previous, next]) => !Quote.equals(previous, next)),
    mergeMap(([, next]) => (next.price ? [next.price] : [])),
    map(
      (newPrice) => `Quote updated, new total: ${formatMoney(newPrice.total)}`,
    ),
    whileActive(isActive$),
  )
}
